import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NumberBall } from '@/components/NumberBall';
import { ConvenienceBar } from '@/components/ConvenienceBar';
import { SEOHead } from '@/components/SEOHead';
import AdBanner from '@/components/AdBanner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowLeft, Calendar, Hash, TrendingUp, TrendingDown, Clock, Target, Award, Flame, Snowflake, Loader2 } from 'lucide-react';
import WinningOdds from '@/components/WinningOdds';
import { API } from '@/config/api';

// Performance logging
const perfLog = (label) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PERF] ${label}: ${new Date().toISOString()}`);
  }
};

function safeFormatDate(rawDate) {
  if (!rawDate) return "No date";

  let dateObj;

  // Backend default: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    const [year, month, day] = rawDate.split("-");
    dateObj = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    const parsed = new Date(rawDate);
    if (isNaN(parsed.getTime())) {
      return rawDate; // Fallback to avoid Invalid Date
    }
    dateObj = parsed;
  }

  return dateObj.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Deferred callback helper - uses requestIdleCallback with fallback
const deferLoad = (callback, delay = 150) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, { timeout: delay + 100 });
  }
  return setTimeout(callback, delay);
};

const cancelDeferLoad = (id) => {
  if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

export const LotteryPage = () => {
  const { lotteryId } = useParams();
  const navigate = useNavigate();
  const { t, currentLang } = useLanguage();
  
  // PHASE 1: Light data for immediate render (overview)
  const [lottery, setLottery] = useState(null);
  const [latestDraws, setLatestDraws] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  
  // PHASE 2: Heavy data loaded after overview (stats)
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Guards to prevent duplicate fetches
  const overviewFetchedRef = useRef(false);
  const statsFetchedRef = useRef(false);
  const deferIdRef = useRef(null);

  // PHASE 1: Fetch light data immediately (render-first)
  useEffect(() => {
    if (overviewFetchedRef.current) return;
    overviewFetchedRef.current = true;
    
    perfLog('shell render');
    
    const fetchOverview = async () => {
      try {
        // Fetch only light endpoints in parallel
        const [lotteryRes, drawsRes] = await Promise.all([
          api.get(`${API}/lotteries/${lotteryId}`),
          api.get(`${API}/lotteries/${lotteryId}/draws?limit=10`)
        ]);
        
        setLottery(lotteryRes.data);
        setLatestDraws(drawsRes.data.draws || []);
        perfLog('overview populated');
      } catch (error) {
        console.error('Error fetching lottery overview:', error);
        navigate(`/${currentLang}`);
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
    
    return () => {
      overviewFetchedRef.current = false;
    };
  }, [lotteryId, currentLang, navigate]);

  // PHASE 2: Defer heavy stats fetch using requestIdleCallback
  useEffect(() => {
    // Only fetch stats after overview is populated
    if (overviewLoading || !lottery || statsFetchedRef.current) return;
    
    statsFetchedRef.current = true;
    setStatsLoading(true);
    
    // Use requestIdleCallback to defer stats loading
    deferIdRef.current = deferLoad(async () => {
      try {
        const statsRes = await api.get(`${API}/lotteries/${lotteryId}/stats`);
        setStats(statsRes.data);
        perfLog('stats populated');
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Don't break the page, just log the error
      } finally {
        setStatsLoading(false);
      }
    }, 200);
    
    return () => {
      if (deferIdRef.current) {
        cancelDeferLoad(deferIdRef.current);
      }
      // Don't reset ref in cleanup - it prevents duplicate fetches
    };
  }, [overviewLoading, lottery, lotteryId]);

  // Memoized derived values to prevent recomputation
  const sortedByConvenience = useMemo(() => {
    if (!stats?.main_numbers?.convenience_index) return [];
    return Object.entries(stats.main_numbers.convenience_index)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, [stats]);

  const sortedByFrequency = useMemo(() => {
    if (!stats?.main_numbers?.frequency) return [];
    return Object.entries(stats.main_numbers.frequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, [stats]);

  const sortedByDelay = useMemo(() => {
    if (!stats?.main_numbers?.delay) return [];
    return Object.entries(stats.main_numbers.delay)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, [stats]);

  // Render shell immediately - no global loading gate
  if (overviewLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-800 rounded" />
          <div className="h-64 bg-gray-800 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!lottery) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="lottery-page">
      <SEOHead 
        title={`${lottery.name} - Statistics & Predictions`}
        description={`View detailed statistics, hot and cold numbers, convenience index, and generate predictions for ${lottery.name}. Analyze ${stats?.total_draws || 0} historical draws.`}
        path={`/lottery/${lotteryId}`}
      />

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/${currentLang}`)}
          className="text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-2xl flex items-center justify-center">
            <span className="text-amber-400 font-bold text-2xl">{lottery.name.charAt(0)}</span>
          </div>
          <div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-white" 
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {lottery.name}
            </h1>
            <p className="text-gray-400 flex items-center gap-2">
              {lottery.country}
              <span className="w-1 h-1 bg-gray-600 rounded-full" />
              <span className="text-amber-400">
                {statsLoading ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading stats...
                  </span>
                ) : (
                  `${stats?.total_draws || 0} ${t('lotteryPage.drawsAnalyzed')}`
                )}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Info Cards - rendered immediately with lottery data */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-gray-900/50 border-gray-700/50">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
              <Hash className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">{t('lottery.numberRange')}</p>
              <p className="text-white font-semibold text-lg">
                {lottery.main_numbers.min}-{lottery.main_numbers.max}
                <span className="text-gray-500 text-sm font-normal ml-2">({lottery.main_numbers.count} numbers)</span>
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-700/50">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">{lottery.bonus_numbers.name}</p>
              <p className="text-white font-semibold text-lg">
                {lottery.bonus_numbers.min}-{lottery.bonus_numbers.max}
                <span className="text-gray-500 text-sm font-normal ml-2">({lottery.bonus_numbers.count})</span>
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-700/50">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">{t('lottery.drawDays')}</p>
              <p className="text-white font-semibold">
                {lottery.draw_days.join(', ')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generate Prediction CTA */}
      <Card className="bg-gradient-to-r from-amber-500/10 via-amber-600/5 to-amber-500/10 border-amber-500/30 mb-8">
        <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-white font-semibold text-lg mb-1">{t('lotteryPage.readyToGenerate')}</h3>
            <p className="text-gray-400 text-sm">{t('lotteryPage.useSmartPrediction', { name: lottery.name })}</p>
          </div>
          <Button 
            asChild
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900 font-semibold rounded-full px-8 shadow-lg shadow-amber-500/25"
            data-testid="generate-prediction-btn"
          >
            <Link to={`/${currentLang}/predictions?lottery=${lotteryId}`}>
              <Target className="w-5 h-5 mr-2" />
              {t('lottery.generatePrediction')}
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Latest Results - rendered immediately with draws data */}
      <Card className="bg-gray-900/50 border-gray-700/50 mb-8">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-400" />
            {t('lottery.latestResults')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {latestDraws.slice(0, 5).map((draw, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-4 py-4 border-b border-gray-800 last:border-0">
                <span className="text-gray-500 text-sm w-28 font-medium">
                  {safeFormatDate(draw.date)}
                </span>
                <div className="flex flex-wrap gap-2 items-center">
                  {draw.main_numbers.map((num, i) => (
                    <NumberBall key={i} number={num} type="main" size="sm" />
                  ))}
                  
                  {/* Handle different bonus types */}
                  {draw.bonus_numbers && draw.bonus_numbers.length > 0 && (
                    <>
                      <span className="w-px h-8 bg-gray-700 mx-1" />
                      {draw.bonus_numbers.map((num, i) => (
                        <NumberBall key={`b-${i}`} number={num} type="bonus" size="sm" />
                      ))}
                    </>
                  )}
                  
                  {/* German Lotto Superzahl */}
                  {draw.superzahl !== null && draw.superzahl !== undefined && (
                    <>
                      <span className="w-px h-8 bg-gray-700 mx-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 mr-1">SZ</span>
                        <NumberBall number={draw.superzahl} type="superzahl" size="sm" />
                      </div>
                    </>
                  )}
                  
                  {/* La Primitiva Reintegro */}
                  {draw.reintegro !== null && draw.reintegro !== undefined && (
                    <>
                      <span className="w-px h-8 bg-gray-700 mx-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 mr-1">R</span>
                        <NumberBall number={draw.reintegro} type="reintegro" size="sm" />
                      </div>
                    </>
                  )}
                  
                  {/* La Primitiva Complementario */}
                  {draw.complementario !== null && draw.complementario !== undefined && (
                    <>
                      <span className="w-px h-8 bg-gray-700 mx-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 mr-1">C</span>
                        <NumberBall number={draw.complementario} type="complementario" size="sm" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ad Banner */}
      <AdBanner size="leaderboard" position="mid-lottery" className="mb-8" />

      {/* Statistics Tabs - show loading state while stats load */}
      <Tabs defaultValue="convenience" className="mb-8">
        <TabsList className="bg-gray-900/80 border border-gray-700 p-1 h-auto flex-wrap">
          <TabsTrigger 
            value="convenience" 
            className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 px-4 py-2"
          >
            <Award className="w-4 h-4 mr-2" />
            {t('lottery.convenienceIndex')}
          </TabsTrigger>
          <TabsTrigger 
            value="hot" 
            className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400 px-4 py-2"
          >
            <Flame className="w-4 h-4 mr-2" />
            {t('lottery.hotNumbers')}
          </TabsTrigger>
          <TabsTrigger 
            value="cold" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 px-4 py-2"
          >
            <Snowflake className="w-4 h-4 mr-2" />
            {t('lottery.coldNumbers')}
          </TabsTrigger>
          <TabsTrigger 
            value="delay" 
            className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 px-4 py-2"
          >
            <Clock className="w-4 h-4 mr-2" />
            {t('lottery.delay')}
          </TabsTrigger>
        </TabsList>

        {/* Stats loading indicator */}
        {statsLoading && (
          <div className="mt-6 flex items-center justify-center py-12 bg-gray-900/30 rounded-xl border border-gray-700/50">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400 mr-3" />
            <span className="text-gray-400">Loading statistics...</span>
          </div>
        )}

        {/* Stats content - only render when stats are loaded */}
        {!statsLoading && stats && (
          <>
            <TabsContent value="convenience" className="mt-6">
              <Card className="bg-gray-900/50 border-gray-700/50">
                <CardHeader>
                  <CardTitle className="text-white">{t('lotteryPage.topByConvenience')}</CardTitle>
                  <p className="text-gray-400 text-sm">{t('lotteryPage.topByConvenienceDesc')}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {sortedByConvenience.map(([num, value], idx) => (
                      <div key={num} className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-xl">
                        <div className="relative">
                          <NumberBall number={parseInt(num)} type="main" size="md" />
                          {idx < 3 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-gray-900 text-xs font-bold rounded-full flex items-center justify-center">
                              {idx + 1}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <ConvenienceBar value={value} size="md" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="hot" className="mt-6">
              <Card className="bg-gray-900/50 border-gray-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Flame className="w-5 h-5 text-red-400" />
                    {t('lotteryPage.mostFrequent')}
                  </CardTitle>
                  <p className="text-gray-400 text-sm">{t('lotteryPage.mostFrequentDesc')}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {stats?.main_numbers?.hot?.map((num, idx) => (
                      <div key={num} className="text-center">
                        <div className="relative">
                          <NumberBall number={num} type="hot" size="lg" />
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                            {idx + 1}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-2">{stats.main_numbers.frequency[num]}x</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cold" className="mt-6">
              <Card className="bg-gray-900/50 border-gray-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Snowflake className="w-5 h-5 text-blue-400" />
                    {t('lotteryPage.leastFrequent')}
                  </CardTitle>
                  <p className="text-gray-400 text-sm">{t('lotteryPage.leastFrequentDesc')}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {stats?.main_numbers?.cold?.map((num, idx) => (
                      <div key={num} className="text-center">
                        <div className="relative">
                          <NumberBall number={num} type="cold" size="lg" />
                        </div>
                        <p className="text-gray-500 text-xs mt-2">{stats.main_numbers.frequency[num]}x</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="delay" className="mt-6">
              <Card className="bg-gray-900/50 border-gray-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-400" />
                    {t('lotteryPage.mostOverdue')}
                  </CardTitle>
                  <p className="text-gray-400 text-sm">{t('lotteryPage.mostOverdueDesc')}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {sortedByDelay.map(([num, delay]) => (
                      <div key={num} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl">
                        <NumberBall number={parseInt(num)} type="main" size="sm" />
                        <span className="text-purple-400 font-semibold">{delay} {t('lotteryPage.draws')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Frequency Table - only show when stats loaded */}
      {!statsLoading && stats && (
        <Card className="bg-gray-900/50 border-gray-700/50">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              {t('lotteryPage.frequencyOverview')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {sortedByFrequency.map(([num, freq]) => (
                <div key={num} className="text-center p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors">
                  <span className="text-white font-bold text-lg">{num}</span>
                  <p className="text-amber-400 text-xs font-medium">{freq}x</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* WINNING ODDS */}
      <WinningOdds lotteryCode={lotteryId} />
    </div>
  );
};

export default LotteryPage;
